'use client';

import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Clock, AlertTriangle, X } from 'lucide-react';
import { useAplicarHorarioEmpresa } from '@/components/hooks/useAplicarHorarioEmpresa';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

type Props = {
  open: boolean;
  onClose: () => void;
  idEmpresa: string;
  idTemporada?: string | null;
  nombreTemporada?: string;
};

export const AplicarHorarioTrabajadoresDialog = ({
  open,
  onClose,
  idEmpresa,
  idTemporada = null,
  nombreTemporada,
}: Props) => {
  const {
    trabajadores,
    loading,
    saving,
    sinHorario,
    fetchTrabajadores,
    toggleTrabajador,
    seleccionarTodos,
    aplicarHorario,
  } = useAplicarHorarioEmpresa(idEmpresa, idTemporada);

  useEffect(() => {
    if (open && idEmpresa) {
      fetchTrabajadores();
    }
  }, [open, idEmpresa, idTemporada, nombreTemporada, fetchTrabajadores]);

  const handleAplicar = async () => {
    const { ok } = await aplicarHorario();
    if (ok) onClose();
  };

  const getIniciales = (nombre: string) =>
    (nombre ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || '?';

  const totalSeleccionados = trabajadores.filter((t) => t.seleccionado).length;
  const todosSeleccionados =
    trabajadores.length > 0 && totalSeleccionados === trabajadores.length;
  const seSobrescriben = trabajadores.filter(
    (t) => t.seleccionado && t.tiene_horario
  );

  const nombresSobrescritos = seSobrescriben
    .map((t) => t.nombre_completo || t.email)
    .join(', ');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              <Clock className="w-5 h-5 shrink-0" />
              {nombreTemporada
                ? `Aplicar el horario de ${nombreTemporada}`
                : 'Aplicar el horario a los trabajadores'}
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          {sinHorario > 0 && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  {sinHorario === 1
                    ? '1 trabajador no tiene esta jornada'
                    : `${sinHorario} trabajadores no tienen esta jornada`}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  {nombreTemporada
                    ? `Durante ${nombreTemporada} ${
                        sinHorario === 1 ? 'seguirá' : 'seguirán'
                      } con su horario general. Márcalos si quieres asignársela.`
                    : 'Sin horario propio no aparecen huecos libres al reservar.'}
                </p>
              </div>
            </div>
          )}

          {!loading && trabajadores.length > 0 && nombreTemporada && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 px-1">
              A la derecha, con qué horario trabaja cada uno{' '}
              <span className="font-semibold">solo durante {nombreTemporada}</span>.
            </p>
          )}

          {!loading && trabajadores.length > 0 && (
            <div className="flex items-center justify-between mb-2 px-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {totalSeleccionados} de {trabajadores.length} seleccionados
              </p>
              <button
                onClick={() => seleccionarTodos(!todosSeleccionados)}
                className="text-xs font-semibold text-[#103254] dark:text-blue-400 hover:underline"
              >
                {todosSeleccionados ? 'Quitar todos' : 'Seleccionar todos'}
              </button>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center h-36">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254] dark:border-blue-400" />
              </div>
            ) : trabajadores.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                No hay trabajadores activos en tu empresa.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-white/10">
                {trabajadores.map((t) => (
                  <li
                    key={t.id_usuario}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                      t.seleccionado
                        ? 'bg-white dark:bg-transparent'
                        : 'bg-gray-50 dark:bg-white/[0.03] opacity-70'
                    }`}
                  >
                    {t.foto ? (
                      <img
                        src={t.foto}
                        alt={t.nombre_completo}
                        className="h-10 w-10 rounded-full object-cover border border-gray-200 dark:border-white/10 shrink-0"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 text-[#103254] dark:text-blue-400 flex items-center justify-center font-bold text-sm shrink-0">
                        {getIniciales(t.nombre_completo)}
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {t.nombre_completo}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {t.email}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* La etiqueta dice con que horario trabaja hoy en esas
                          fechas, no si tiene o no franjas. */}
                      <span
                        className={`text-xs font-medium text-right max-w-[7.5rem] truncate ${
                          t.tiene_horario
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-amber-600 dark:text-amber-400'
                        }`}
                        title={
                          t.tiene_horario
                            ? nombreTemporada ?? 'Con horario'
                            : nombreTemporada
                              ? 'Horario general'
                              : 'Sin horario'
                        }
                      >
                        {t.tiene_horario
                          ? (nombreTemporada ?? 'Con horario')
                          : nombreTemporada
                            ? 'Horario general'
                            : 'Sin horario'}
                      </span>
                      <Switch
                        checked={t.seleccionado}
                        onCheckedChange={(checked) =>
                          toggleTrabajador(t.id_usuario, checked)
                        }
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {seSobrescriben.length > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              {nombreTemporada
                ? `${nombresSobrescritos} ${
                    seSobrescriben.length === 1 ? 'ya tenía' : 'ya tenían'
                  } una jornada guardada para ${nombreTemporada}. Al aplicar se sustituye por la que acabas de definir.`
                : `${nombresSobrescritos} ${
                    seSobrescriben.length === 1 ? 'ya tenía' : 'ya tenían'
                  } horario propio. Al aplicar se sustituye por el que acabas de definir.`}
            </p>
          )}
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            Omitir
          </Button>
          <Button
            onClick={handleAplicar}
            disabled={saving || loading || totalSeleccionados === 0}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {saving ? 'Aplicando…' : 'Aplicar horario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};